import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type {
  ActorRole,
  ActorRolesResponse,
  ActorRegistrationResult,
  RegisteredActor,
  StoredActorRegistration,
} from "~/api/actorTypes";

// Actor registry:
//   GET  /api/v2/Actors/roles
//   POST /api/v2/Actors/register
//   GET  /api/v2/Actors/{actorUserId}
//
// Registration is scoped PER REUNIFICATION PROCESS, not per device. Shared
// tablets get handed between staff during an incident, and a device-wide GUID
// would attribute the whole event to whoever registered first. What clears a
// registration is a process change — not a tab close — so the map below is
// keyed by process id and persisted.

// The four roles the backend published. Only a fallback until GET /roles
// answers (or when it fails) — the server list is authoritative and is what
// gets validated against. The registration prompt no longer renders a role
// picker (the surface fixes the role), so nothing in the UI reads this today.
const FALLBACK_ROLES: ActorRole[] = [
  "Ambassador",
  "Greeter",
  "Reunifier",
  "Admin",
];

export const useActorsStore = defineStore(
  "actors",
  () => {
    const config = useRuntimeConfig();
    const apiBaseUrl = computed(() => config.public.apiBaseUrl || "");
    const getApiUrl = (endpoint: string) =>
      `${apiBaseUrl.value}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

    // processId → the actor registered on this device for that process.
    const registrations = ref<Record<string, StoredActorRegistration>>({});
    const roles = ref<ActorRole[]>([]);
    const rolesLoaded = ref(false);
    const registering = ref(false);

    // Which process the dashboard is currently showing. Set on entry so
    // useFindMeActor can resolve the acting identity without every caller
    // having to thread a process id through.
    const currentProcessId = ref<string | null>(null);

    const roleOptions = computed<ActorRole[]>(() =>
      roles.value.length ? roles.value : FALLBACK_ROLES,
    );

    const registrationFor = (
      reunificationProcessId?: string | null,
    ): StoredActorRegistration | null =>
      reunificationProcessId
        ? registrations.value[reunificationProcessId] ?? null
        : null;

    const currentRegistration = computed(() =>
      registrationFor(currentProcessId.value),
    );

    /** True when this device still needs to ask who is working the incident. */
    const needsRegistration = (reunificationProcessId?: string | null) =>
      Boolean(reunificationProcessId) &&
      registrationFor(reunificationProcessId) === null;

    async function fetchRoles(force = false): Promise<ActorRole[]> {
      if (rolesLoaded.value && !force) return roleOptions.value;
      try {
        const response = await fetch(getApiUrl("api/v2/Actors/roles"));
        if (!response.ok) {
          throw new Error(`Failed to fetch actor roles: ${response.statusText}`);
        }
        const data = (await response.json()) as ActorRolesResponse;
        if (Array.isArray(data?.roles) && data.roles.length) {
          roles.value = data.roles;
        }
        rolesLoaded.value = true;
      } catch (err) {
        // Non-fatal: the select falls back to the published four. Registration
        // still validates server-side, so a stale list surfaces as a 400 with
        // the allowed values rather than as a silently wrong role.
        console.warn("Actor roles unavailable, using fallback list:", err);
      }
      return roleOptions.value;
    }

    /**
     * Register (or re-register) the person working this process.
     *
     * Idempotent on actorUserId server-side, so re-sending after a reload or a
     * retry updates the row rather than creating a second one — which is why
     * an existing GUID for the process is reused instead of minted afresh.
     */
    async function register(
      reunificationProcessId: string,
      details: {
        name: string;
        role: ActorRole;
        phoneNumber?: string | null;
        email?: string | null;
      },
    ): Promise<ActorRegistrationResult> {
      registering.value = true;
      try {
        const existing = registrationFor(reunificationProcessId);
        const actorUserId = existing?.actorUserId ?? crypto.randomUUID();

        const response = await fetch(getApiUrl("api/v2/Actors/register"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            actorUserId,
            name: details.name,
            role: details.role,
            reunificationProcessId,
            phoneNumber: details.phoneNumber ?? null,
            email: details.email ?? null,
          }),
        });

        if (!response.ok) {
          let errorMessage = `Registration failed: ${response.statusText}`;
          let allowedRoles: ActorRole[] | undefined;
          try {
            const body = await response.json();
            errorMessage = body?.errorMessage || body?.title || errorMessage;
            // A 400 says which roles would have been accepted — adopt them so
            // the select stops offering the value that was just rejected.
            if (Array.isArray(body?.allowedRoles)) {
              allowedRoles = body.allowedRoles;
              roles.value = body.allowedRoles;
              rolesLoaded.value = true;
            }
          } catch {
            // non-JSON error body — keep statusText
          }
          return { ok: false, errorMessage, allowedRoles };
        }

        // The response echoes the stored row: { id, name, role,
        // reunificationProcessId, registeredAt, apiVersion }. Prefer the echoed
        // values — role in particular comes back CANONICALISED ("ambassador" in,
        // "Ambassador" out), and the canonical casing is what the find-me
        // `actors` map and the audit trail use.
        const body = await response.json().catch(() => null);
        const actor: RegisteredActor = {
          actorUserId: body?.actorUserId ?? body?.id ?? actorUserId,
          name: body?.name ?? details.name,
          role: body?.role ?? details.role,
          reunificationProcessId,
          phoneNumber: body?.phoneNumber ?? details.phoneNumber ?? null,
          email: body?.email ?? details.email ?? null,
        };

        registrations.value = {
          ...registrations.value,
          [reunificationProcessId]: {
            actorUserId: actor.actorUserId,
            // Kept only to show staff who they are signed in as. The server
            // resolves names from the GUID at read time, so this copy is never
            // sent on a command.
            name: actor.name,
            role: actor.role,
            registeredAt: new Date().toISOString(),
          },
        };

        return { ok: true, actor };
      } catch (err) {
        return {
          ok: false,
          errorMessage: err instanceof Error ? err.message : "Network error",
        };
      } finally {
        registering.value = false;
      }
    }

    /** Look an actor up by GUID. Returns null on 404 (not registered). */
    async function fetchActor(
      actorUserId: string,
    ): Promise<RegisteredActor | null> {
      try {
        const response = await fetch(
          getApiUrl(`api/v2/Actors/${encodeURIComponent(actorUserId)}`),
        );
        if (response.status === 404) return null;
        if (!response.ok) {
          throw new Error(`Failed to fetch actor: ${response.statusText}`);
        }
        return (await response.json()) as RegisteredActor;
      } catch (err) {
        console.error("Error fetching actor:", err);
        return null;
      }
    }

    /** Forget the registration for a process (e.g. "not me — switch user"). */
    function clearRegistration(reunificationProcessId: string) {
      registrations.value = Object.fromEntries(
        Object.entries(registrations.value).filter(
          ([processId]) => processId !== reunificationProcessId,
        ),
      );
    }

    function setCurrentProcess(reunificationProcessId: string | null) {
      currentProcessId.value = reunificationProcessId;
    }

    return {
      registrations,
      roles,
      roleOptions,
      registering,
      currentProcessId,
      currentRegistration,
      registrationFor,
      needsRegistration,
      fetchRoles,
      register,
      fetchActor,
      clearRegistration,
      setCurrentProcess,
    };
  },
  {
    // Survives a reload so a refreshed tablet does not re-prompt mid-incident.
    // Only the per-process map is persisted: the roles list is re-fetched, and
    // currentProcessId is set from the route on entry.
    persist: {
      key: "redbag-actors",
      storage: typeof window !== "undefined" ? localStorage : undefined,
      pick: ["registrations"],
    },
  },
);
